import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import usePageMeta from "@/hooks/usePageMeta";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DetailHero from "@/pages/detail/components/DetailHero";
import ChapterList from "@/pages/detail/components/ChapterList";
import AnimeCard from "@/components/feature/AnimeCard";
import { getAnimeById, getRelatedAnime, animeList } from "@/mocks/anime";
import { readFavorites, toggleFavorite } from "@/lib/favorites";

export default function DetailPage() {
  const { id } = useParams();
  const anime = getAnimeById(id);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    setIsFav(anime ? readFavorites().includes(anime.id) : false);
  }, [anime]);

  usePageMeta(
    anime ? `妖精动漫 - 动漫《${anime.title}》详情介绍` : "妖精动漫 - 作品未找到",
    anime
      ? `${anime.title}（${anime.originalTitle}）是${anime.year}年的${anime.region}${anime.type}动画，共${anime.episodes}话，当前${anime.status}。${anime.summary.slice(0, 60)}`
      : "抱歉，没有找到对应的动漫作品，可以返回分类页浏览其他作品。"
  );

  if (!anime) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 text-[13px] text-foreground-500">可能链接有误，或者作品已经下架演示。</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/category/guoman" className="h-10 px-5 inline-flex items-center rounded-md bg-primary-900 text-background-50 text-[13px] font-semibold whitespace-nowrap cursor-pointer">
              浏览动漫分类
            </Link>
            <Link to="/" className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-[13px] font-medium whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedAnime(anime, 4);
  const alsoLike = animeList.filter((a) => a.id !== anime.id && a.region === anime.region).slice(0, 4);
  const fallbackLike = alsoLike.length >= 4 ? alsoLike : animeList.filter((a) => a.id !== anime.id).slice(0, 4);

  const handleToggleFav = () => {
    setIsFav(toggleFavorite(anime.id));
  };

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 pt-5 [&_a]:text-primary-300 [&_span]:text-primary-100">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "动漫分类", to: "/category/guoman" },
              { label: anime.region === "国漫" ? "国漫" : anime.region === "日漫" ? "日漫" : "韩漫改", to: `/category/${anime.region === "国漫" ? "guoman" : anime.region === "日漫" ? "riman" : "hanman"}` },
              { label: anime.title },
            ]}
          />
        </div>
      </div>
      <DetailHero anime={anime} isFav={isFav} onToggleFav={handleToggleFav} />

      <section className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 lg:gap-8">
          <div className="min-w-0">
            <ChapterList anime={anime} />

            <div className="mt-8">
              <h2 className="text-lg md:text-xl font-black text-foreground-950 tracking-tight mb-4">内容简介</h2>
              <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
                <p className="text-[13px] md:text-[14px] leading-7 text-foreground-700">{anime.summary}</p>
                <p className="mt-3 text-[13px] md:text-[14px] leading-7 text-foreground-700">
                  本作由 {anime.studio} 制作，{anime.director} 执导，题材融合 {anime.tags.join("、")} 等元素。
                  {anime.status === "连载中"
                    ? `目前正在 ${anime.serialDay} 更新，适合喜欢边追边讨论的观众。`
                    : "目前已经全部完结，可以一次性补完整个故事。"}
                  站内提供完整的章节信息与简介，方便你在决定观看前快速了解作品调性。
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "话数", value: `${anime.episodes} 话` },
                    { label: "年份", value: `${anime.year}` },
                    { label: "状态", value: anime.status },
                    { label: "评分", value: `${anime.rating.toFixed(1)} 分` },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md bg-background-50 border border-background-200 px-3 py-2">
                      <p className="text-[11px] text-foreground-400">{item.label}</p>
                      <p className="mt-0.5 text-[13px] font-semibold text-foreground-900">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-[88px] lg:self-start space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4">
              <h2 className="text-[14px] font-bold text-foreground-950 mb-3">相关推荐</h2>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link key={item.id} to={`/detail/${item.id}`} className="flex gap-2.5 group cursor-pointer">
                    <div className="w-12 h-[68px] rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title}推荐缩略图`}
                        title={`${item.title} - 相关推荐`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-500">{item.type} · {item.region}</p>
                      <p className="mt-0.5 text-[11px] text-accent-700 font-semibold">{item.rating.toFixed(1)} 分</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-4">
              <h2 className="text-[13px] font-bold text-secondary-950">观看提示</h2>
              <ul className="mt-2 space-y-2 text-[12px] leading-5 text-secondary-900">
                <li className="flex gap-1.5"><i className="ri-check-line mt-0.5" />章节信息与简介为演示数据</li>
                <li className="flex gap-1.5"><i className="ri-check-line mt-0.5" />播放入口不会加载真实视频</li>
                <li className="flex gap-1.5"><i className="ri-check-line mt-0.5" />收藏记录仅保存在本地浏览器</li>
              </ul>
            </div>
          </aside>
        </div>

        {fallbackLike.length > 0 && (
          <div className="mt-10 md:mt-14">
            <div className="flex items-end justify-between gap-4 mb-5">
              <h2 className="text-lg md:text-xl font-black text-foreground-950 tracking-tight">你可能也喜欢</h2>
              <Link to="/category/riman" className="text-[13px] font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer">
                查看更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {fallbackLike.map((item) => (
                <AnimeCard key={item.id} anime={item} />
              ))}
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}